import React, { useEffect, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import Scrollable from "../Shared/Edit/Scrollable";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { callApiWithAuthorization } from "../../API/CallApi";
import Loader from "../Shared/Loader/loader";
import RemoveDialog from "../Shared/Remove/RemoveDialog";
import { OverlayLoader } from "../Shared/Loader/OverlayLoader";
import AttributeTypes from "./AttributeTypes";
import EditSharedPropertyDialog from "./EditSharedPropertyDialog";
import { useTranslation } from "react-i18next";
import { getSharedLookups } from "../../API/SharedLookup/SharedLookup";
import { createAttribute } from "../../API/Attribute/Attribute";

const SelectSharedProperty = ({
    handleClose,
    openCreateSharedPropertyDialog,
    onCreateSharedProperty,
    updatedSharedProperties,
    removeSharedPropertyAttribute,
    project,
    entityId,
    subscriptionId,
    attributes
}) => {

    const Fields = AttributeTypes.Fields;
    const DataTypes = AttributeTypes.Types;
    const sharedPropertyTypes = AttributeTypes.Types.filter((item) => item.value === "Picklist String" || item.value === "Tree Dropdown");

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creatingSharedProperty, setCreatingSharedProperty] = useState(false);
    const [showDeleteSharedPropertyDialog, setShowDeleteSharedPropertyDialog] = useState(false);
    const [deletingSharedProperty, setDeletingSharedProperty] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [showEditSharedPropertyDialog, setShowEditSharedPropertyDialog] = useState(false);
    const [editId, setEditId] = useState(null);
    const [searchValue, setSearchValue] = useState("");

    const { t, ready: tReady } = useTranslation([
        "attribute/shared_property",
    ]);

    // Fetch shared properties on component mount
    useEffect(() => {
        getSharedProperties();
    }, []);

    // Fetch shared properties
    const getSharedProperties = async () => {
        try {
            let response = await getSharedLookups(project._id);
            const modifiedData = response.map((item, index) => ({
                ...item,
                indexId: index + 1
            }));
            modifiedData.reverse();
            setData(modifiedData);
            updatedSharedProperties(modifiedData);
        } catch (error) {
            console.error('Error fetching shared properties:', error);
        } finally {
            setLoading(false);
        }
    };

    // Open delete shared property dialog and set delete id
    const openDeleteSharedPropertyDialog = (id) => {
        setShowDeleteSharedPropertyDialog(true);
        setDeleteId(id);
    };

    // Close delete shared property dialog and reset delete id
    const closeDeleteSharedPropertyDialog = () => {
        setShowDeleteSharedPropertyDialog(false);
        setDeleteId(null);
    };

    // open edit shared property dialog and set edit id
    const openEditSharedPropertyDialog = (id) => {
        setShowEditSharedPropertyDialog(true);
        setEditId(id);
    };

    // Close edit shared property dialog and reset edit id
    const closeEditSharedPropertyDialog = () => {
        setShowEditSharedPropertyDialog(false);
        setEditId(null);
    };

    // Add selected shared property to the list
    const addSelectedSharedProperty = async (dataItem) => {
        try {
            dataItem.name = dataItem.name.trim();
            setCreatingSharedProperty(true);

            let order = 10000000000;

            if (attributes.length > 0) {
                let highestOrder = 10000000000;
                for (let attribute of attributes) {
                    const orderVal = parseInt(attribute.order);
                    if (orderVal > highestOrder) {
                        highestOrder = orderVal;
                    }
                }
                order = highestOrder + 10000000000;
            }

            let fieldData = Fields[dataItem.type];

            let properties = {};

            for (let item of fieldData) {
                properties[item.field] = item.defaultValue;
            }

            let counter = 0;
            const initialName = dataItem.name;
            let finalName = initialName;
            for (let attributeIndex = 0; attributeIndex < attributes.length; attributeIndex++) {
                if (attributes[attributeIndex].attributeName === finalName) {
                    ++counter;
                    finalName = initialName + "(" + counter + ")";
                }
            }

            properties.attributeName = finalName;
            properties.attributeLabel = finalName;
            properties.attributeType = dataItem.type;
            properties.sharedLookupType = dataItem.sharedLookupType;
            properties.sharedLookupId = dataItem._id;
            properties.description = finalName;
            properties.tooltip = finalName;
            properties.required = false;
            properties.readOnly = false;
            properties.order = String(order);
            properties.sysName = dataItem.sysName;
            if (dataItem.isGlobal) {
                properties.usedGlobalSharedProperty = true;
            }

            console.time("Create attribute");
            const attribute = await createAttribute(entityId, {
                subscriptionId: subscriptionId,
                projectId: project._id,
                properties
            })
            console.timeEnd("Create attribute");
            if (attribute.status && attribute.status === "Failure") {
                handleClose(true);
                setCreatingSharedProperty(false);
                return;
            }

            attribute.typeText = DataTypes.find((type) => type.value === dataItem.type).text;
            attribute.required = attribute.required === true;
            attribute.readOnly = attribute.readOnly === true;
            attribute.multiSelect = attribute.multiSelect === true;
            attribute.indexId = attributes.length + 1;
            attribute.sharedLookupId = dataItem._id;
            attribute.sharedLookupType = dataItem.sharedLookupType;
            await onCreateSharedProperty(attribute);
        } catch (error) {
            console.error("Error occurred:", error);
            handleClose(false);
        } finally {
            setCreatingSharedProperty(false);
        }
    };

    // Filter data based on search value
    const filterData = () => {

        let filteredData = data.filter((item) => item.name.toLowerCase().includes(searchValue.toLowerCase()));
        filteredData = filteredData.map((item, index) => ({
            ...item,
            indexId: index + 1
        }));
        return filteredData;
    };

    // Delete shared property
    const deleteSharedProperty = async () => {
        try {
            if (deleteId) {
                setDeletingSharedProperty(true);
                await callApiWithAuthorization(`/api/v0/project/deleteSharedLookup/${deleteId}`, {
                    method: "DELETE",
                });
                const modifiedData = data.filter(item => item._id !== deleteId);
                setData(modifiedData);
                updatedSharedProperties(modifiedData);
                removeSharedPropertyAttribute(deleteId);
            }
        } catch (error) {
            console.error('Error deleting shared property:', error);
        } finally {
            setDeletingSharedProperty(false);
            closeDeleteSharedPropertyDialog();
        }
    };

    const shouldAddSharedPropertyDisabled = (dataItem) => {
        const sharedPropertyAttributes = attributes.filter((attribute) => attribute.sharedLookupId);
        const disabledAddSharedProperty = sharedPropertyAttributes.some((attribute) => attribute.sharedLookupId === dataItem._id);
        return disabledAddSharedProperty || creatingSharedProperty || deletingSharedProperty;
    };

    // Show loader if data is loading
    if (loading) {
        return <Loader />
    }

    // Return shared property dialog
    return (
        <Dialog
            title={t("title")}
            onClose={() => handleClose(false)}
            width="70%"
            height="70%"
            className="dialog"
        >
            {creatingSharedProperty && <OverlayLoader />}
            {deletingSharedProperty && <OverlayLoader />}
            {showDeleteSharedPropertyDialog &&
                <RemoveDialog
                    showLoader={deletingSharedProperty}
                    Title={t("delete_dialog.title")}
                    toggleDeleteDialog={closeDeleteSharedPropertyDialog}
                    message={t("delete_dialog.message", { name: data.find(sp => sp._id === deleteId).name })}
                    onDelete={deleteSharedProperty}
                    onCancel={closeDeleteSharedPropertyDialog}
                    deleteButtonText={t("delete_dialog.buttons.delete_button_label")}
                    cancelButtonText={t("delete_dialog.buttons.cancel_button_label")}
                />
            }
            {showEditSharedPropertyDialog &&
                <EditSharedPropertyDialog
                    sharedPropertyId={editId}
                    project={project}
                    handleClose={closeEditSharedPropertyDialog}
                    sharedProperties={data}
                    updatedSharedProperties={(data, id) => {
                        setData(data);
                        updatedSharedProperties(data, id);
                    }}
                />
            }
            <Scrollable>
                <div class="d-flex justify-content-between">
                    <button
                        className="btn btn-secondary"
                        onClick={openCreateSharedPropertyDialog}
                    >
                        <i className="fa fa-plus me-2" />
                        {t("create_shared_property_button_label")}
                    </button>
                    <input
                        className="form-control searchBar-width"
                        onChange={(e) => setSearchValue(e.target.value)}
                        value={searchValue}
                        type="text"
                        placeholder={t("search_shared_property_placeholder")}
                    />
                </div>
                <div className="mt-3">
                    <Grid
                        className="no-alt"
                        data={filterData()}
                        selectedField="selected"
                    >
                        <Column field="indexId" title="#" width={33} />
                        <Column
                            title={t("grid.name")}
                            field="name"
                            cell={(props) => (
                                <td>
                                    {props.dataItem.name}
                                    { props.dataItem.isGlobal && <span className="text-muted"> (Global)</span>}
                                </td>
                            )}
                        />
                        {/* <Column field="type" title={t("grid.type")}
                            cell={(props) => (
                                <td>
                                    {DataTypes.find((item) => item.value === props.dataItem.type)?.text}
                                </td>
                            )}
                        /> */}
                        <Column
                            field="sharedLookupType"
                            title={t("grid.shared_property_type")}
                            cell={(props) => (
                                <td>
                                    {sharedPropertyTypes.find((item) => item.value === props.dataItem.sharedLookupType)?.text}
                                </td>
                            )}
                        />
                        <Column
                            width={200}
                            cell={(props) => (
                                <td className="text-end">
                                    <button
                                        onClick={() => addSelectedSharedProperty(props.dataItem)}
                                        disabled={shouldAddSharedPropertyDisabled(props.dataItem)}
                                        className="btn btn-link pe-1 text-decoration-none pointer"
                                    >
                                        <i className="pe-1 fa-solid fa-plus"></i>{t("grid.add_shared_property_button_label")}
                                    </button>
                                    <button
                                        disabled={deletingSharedProperty || creatingSharedProperty}
                                        onClick={() => openEditSharedPropertyDialog(props.dataItem._id)}
                                        className="btn btn-link pe-1 text-decoration-none pointer"
                                    >
                                        <i className="pe-1 fas fa-pencil text-primary"></i>
                                    </button>
                                    <button
                                        disabled={deletingSharedProperty || creatingSharedProperty}
                                        onClick={() => openDeleteSharedPropertyDialog(props.dataItem._id)}
                                        className="btn btn-link text-decoration-none pointer"
                                    >
                                        <i className="pe-1 fas fa-trash text-danger"></i>
                                    </button>
                                </td>
                            )}
                        />
                    </Grid>
                </div>
            </Scrollable>
            <DialogActionsBar layout="end">
                <div className="m-3">
                    <Button
                        onClick={() => handleClose(false)}
                        className="btn btn-secondary"
                    >
                        {t("cancel_button_label")}
                    </Button>
                </div>
            </DialogActionsBar>
        </Dialog>
    );
}
export default SelectSharedProperty;